import type { ComponentProps } from 'react'
import React from 'react'

import { Calendar } from '@fourthwall/components'

import { useShopTime } from '../providers/ShopTimeProvider'
import { addTimezoneAbbreviationToLabels } from '../utils/addTimezoneAbbreviationToLabels'
import { convertDateToLocalDate } from '../utils/convertDateToLocalDate'
import { convertDateToUTC } from '../utils/convertDateToUTC'

type TimezonedCalendarProps = ComponentProps<typeof Calendar>

export const TimezonedCalendar = ({
  selectedDates,
  timeLabels,
  minDate,
  maxDate,
  onDatesChange,
  showTimeInputs = true,
  ...props
}: TimezonedCalendarProps) => {
  const { timezoneAbbreviation, formatInShopTime, toUTC } = useShopTime()

  const convertDatesToLocalDates = (
    dates: TimezonedCalendarProps['selectedDates'],
  ): TimezonedCalendarProps['selectedDates'] => {
    return dates
      .map((date) => convertDateToLocalDate(date, formatInShopTime))
      .filter((date): date is Date => date !== undefined)
  }

  const handleDatesChange = (changedDates: Date[]) => {
    if (!onDatesChange) return

    onDatesChange(changedDates.map((date) => convertDateToUTC(date, toUTC)))
  }

  const getInitialTimeValues = () => {
    if (!showTimeInputs || !!selectedDates.length) return

    const currentLocalTime = formatInShopTime(new Date(), 'hh:mm A')

    return props.type === 'range' ? [currentLocalTime, currentLocalTime] : [currentLocalTime]
  }

  return (
    <Calendar
      {...props}
      selectedDates={convertDatesToLocalDates(selectedDates)}
      timeLabels={
        timeLabels ? addTimezoneAbbreviationToLabels(timeLabels, timezoneAbbreviation) : undefined
      }
      showTimeInputs={showTimeInputs}
      initialTimeValues={getInitialTimeValues()}
      minDate={convertDateToLocalDate(minDate, formatInShopTime)}
      maxDate={convertDateToLocalDate(maxDate, formatInShopTime)}
      onDatesChange={handleDatesChange}
    />
  )
}
